import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import usePersistentFilters from './usePersistentFilters';
import { AuthContext } from '../authentication/AuthContext';

const STATUS_OPTIONS = ['All', 'Loadable', 'Not Loadable'];
const DEFAULT_STATUS = 'All';

const getInitialPersistedFilters = defaultAffiliate => ({
  affiliate: defaultAffiliate,
  location: [],
  product: [],
  status: DEFAULT_STATUS,
});

const getInitialFilters = defaultAffiliate => ({
  ...getInitialPersistedFilters(defaultAffiliate),
  search: '',
});

const normalizePersistedFilters = (filters, defaultAffiliate = null) => ({
  affiliate: filters?.affiliate || defaultAffiliate,
  location: Array.isArray(filters?.location) ? filters.location : [],
  product: Array.isArray(filters?.product) ? filters.product : [],
  status: filters?.status || DEFAULT_STATUS,
});

const normalizeFilters = (filters, defaultAffiliate = null) => ({
  ...getInitialFilters(defaultAffiliate),
  ...filters,
  affiliate: filters?.affiliate || defaultAffiliate,
  location: Array.isArray(filters?.location) ? filters.location : [],
  product: Array.isArray(filters?.product) ? filters.product : [],
  search: filters?.search || '',
  status: filters?.status || DEFAULT_STATUS,
});

/**
 * Custom hook for managing authorization sequencing filters with draft/applied pattern.
 * Implements server-side filtering with validation requiring at least 1 product and 1 location.
 */
export default function useAuthSequencingFilters() {
  const { affiliates = [] } = useContext(AuthContext);
  const defaultAffiliate = useMemo(
    () => affiliates.find(a => a?.supplier) || affiliates[0] || null,
    [affiliates]
  );

  const affiliateOptions = useMemo(
    () => affiliates.filter(affiliate => affiliate?.supplier),
    [affiliates]
  );

  const initialPersistedFilters = useMemo(
    () => getInitialPersistedFilters(defaultAffiliate),
    [defaultAffiliate]
  );

  // Persist only non-search filters in local storage
  const [persistedFilters, setPersistedFilters] = usePersistentFilters(
    'authSequencingFilters',
    initialPersistedFilters
  );

  // Applied filter state (what is actually used in API queries)
  const [appliedFilters, setAppliedFilters] = useState(
    normalizeFilters(persistedFilters, defaultAffiliate)
  );

  // Draft filter state (what user is currently editing in the UI)
  const [draftFilters, setDraftFilters] = useState(
    normalizeFilters(persistedFilters, defaultAffiliate)
  );

  useEffect(() => {
    if (!defaultAffiliate) return;

    setDraftFilters(prev =>
      prev?.affiliate
        ? normalizeFilters(prev, defaultAffiliate)
        : normalizeFilters(getInitialFilters(defaultAffiliate), defaultAffiliate)
    );

    setAppliedFilters(prev =>
      prev?.affiliate
        ? normalizeFilters(prev, defaultAffiliate)
        : normalizeFilters(getInitialFilters(defaultAffiliate), defaultAffiliate)
    );

    setPersistedFilters(prev =>
      prev?.affiliate
        ? normalizePersistedFilters(prev, defaultAffiliate)
        : normalizePersistedFilters(getInitialFilters(defaultAffiliate), defaultAffiliate)
    );
  }, [defaultAffiliate, setPersistedFilters]);

  // Validation: at least 1 product must be selected
  const hasMinimumFilters = useMemo(() => draftFilters.product.length > 0, [draftFilters.product]);

  const canFetchData = useMemo(() => appliedFilters.product.length > 0, [appliedFilters.product]);

  // Check if there are unsaved filter changes
  const hasChanges = useMemo(
    () => JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters),
    [draftFilters, appliedFilters]
  );

  // Check if filters are in their default state
  const isOnDefaultState = useMemo(() => {
    const isResetState = filters => {
      const normalized = normalizeFilters(filters, defaultAffiliate);
      return (
        !normalized.location.length &&
        !normalized.product.length &&
        !normalized.search &&
        normalized.status === DEFAULT_STATUS
      );
    };

    return isResetState(draftFilters) && isResetState(appliedFilters);
  }, [appliedFilters, defaultAffiliate, draftFilters]);

  // Handle individual filter field changes
  const handleChange = useCallback(
    field => (event, newValue) => {
      setDraftFilters(prev => ({ ...prev, [field]: newValue }));
    },
    []
  );

  //  Setters for each field
  const setAffiliateFilter = useCallback(
    value => {
      const resetState = {
        affiliate: value,
        location: [],
        product: [],
        search: '',
        status: DEFAULT_STATUS,
      };
      setDraftFilters(resetState);
      setAppliedFilters(resetState);
      setPersistedFilters(normalizePersistedFilters(resetState, defaultAffiliate));
    },
    [defaultAffiliate, setPersistedFilters]
  );

  const setProductFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, product: value || [] })),
    []
  );

  const setLocationFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, location: value || [] })),
    []
  );

  const setStatusFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, status: value || DEFAULT_STATUS })),
    []
  );

  const setSearch = useCallback(value => setDraftFilters(prev => ({ ...prev, search: value })), []);

  // Apply draft filters (copies draft to applied and persists)
  const handleApply = useCallback(() => {
    const normalizedDraft = normalizeFilters(draftFilters, defaultAffiliate);
    setDraftFilters(normalizedDraft);
    setAppliedFilters(normalizedDraft);
    setPersistedFilters(normalizePersistedFilters(normalizedDraft, defaultAffiliate));
  }, [draftFilters, defaultAffiliate, setPersistedFilters]);

  // Reset all filters to default state
  const handleReset = useCallback(() => {
    const selectedAffiliate = draftFilters?.affiliate || defaultAffiliate;
    const resetFilters = normalizeFilters(
      { ...getInitialFilters(selectedAffiliate), affiliate: selectedAffiliate },
      defaultAffiliate
    );
    setDraftFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setPersistedFilters(normalizePersistedFilters(resetFilters, defaultAffiliate));
  }, [defaultAffiliate, draftFilters?.affiliate, setPersistedFilters]);

  return {
    affiliateOptions,
    appliedFilters,
    canFetchData,
    draftFilters,
    handleApply,
    handleChange,
    handleReset,
    hasChanges,
    hasMinimumFilters,
    isOnDefaultState,
    setAffiliateFilter,
    setLocationFilter,
    setProductFilter,
    setSearch,
    setStatusFilter,
    statusOptions: STATUS_OPTIONS,
  };
}
